// Cross-tenant shared-engagement list + confirm (C6.0 surface) — read via api-client →
// tenant-worker /shared/engagements (visible where the active tenant is a party, RLS
// is_visible_shared_engagement). The Confirm action completes the C6.0 mutual-confirmation
// loop (the counterparty acknowledges the engagement). PLAN-C1.11 / C6.0.
// Rebuilt onto the shadcn design system (W34 PATH-B); same queries/mutations, same data.
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { humanize } from "../lib/format";
import { DataTable, type Column } from "@/components/data-table";

type Row = Record<string, unknown>;

function statusOf(row: Row): string {
  return String(row.status ?? row.engagementStatus ?? "");
}
function isConfirmed(row: Row): boolean {
  const s = statusOf(row);
  return s === "confirmed" || s === "active";
}
function format(v: unknown): string {
  if (v == null) return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

export function EngagementsRoute() {
  const qc = useQueryClient();
  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ["shared-engagements"],
    queryFn: () => api.listSharedEngagements(),
  });
  const confirm = useMutation({
    mutationFn: (engagementId: string) => api.confirmSharedEngagement(engagementId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["shared-engagements"] }),
  });

  const rows = (data ?? []) as Row[];
  // Derive display columns from the first row (excluding id + status, which are rendered specially).
  const dataKeys = rows.length
    ? Object.keys(rows[0]!).filter((c) => c !== "id" && c !== "status" && c !== "engagementStatus").slice(0, 4)
    : [];

  const columns: Column<Row>[] = [
    ...dataKeys.map((key): Column<Row> => ({ key, header: key, render: (row) => format(row[key]) })),
    {
      key: "__status",
      header: "Status",
      render: (row) => {
        const s = statusOf(row);
        if (!s) return <span className="text-muted-foreground">—</span>;
        return <Badge variant={isConfirmed(row) ? "default" : "secondary"}>{humanize(s)}</Badge>;
      },
    },
  ];

  return (
    <div>
      <PageHeader
        title="Engagements"
        description="Shared processor engagements visible to the active tenant context. Confirm to acknowledge a pending engagement (C6.0 mutual confirmation)."
        actions={isFetching ? <span className="text-sm text-muted-foreground">updating…</span> : undefined}
      />

      {error && (
        <p className="mb-4 text-sm text-destructive">Failed to load: {(error as Error).message}</p>
      )}
      {confirm.isError && (
        <p className="mb-4 text-sm text-destructive">{(confirm.error as Error).message}</p>
      )}

      <DataTable<Row>
        columns={columns}
        rows={rows}
        loading={isLoading}
        getRowId={(row) => (row.id as string) ?? JSON.stringify(row)}
        empty="No shared engagements visible."
        rowActions={(row) => {
          const id = row.id as string | undefined;
          if (!id || isConfirmed(row)) return null;
          return (
            <Button size="sm" variant="outline" disabled={confirm.isPending} onClick={() => confirm.mutate(id)}>
              Confirm
            </Button>
          );
        }}
      />
    </div>
  );
}
